import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api.js";

/**
 * „Sicht übernehmen" — die Oberflächenseite.
 *
 * Nach jedem Wechsel wird der **gesamte** Query-Cache verworfen, nicht nur
 * `auth/me`: Ab dem Wechsel gelten andere Rollen und andere Sichtbarkeiten,
 * und jede zwischengespeicherte Liste stammt noch von der vorigen Identität.
 * Genau diese Vermischung war schon einmal ein Befund im Statusbericht (B1) —
 * hier wäre sie schlimmer, weil sie zwei Menschen betrifft.
 */

export interface AssumableEmployee {
  employeeId: string;
  email: string;
  fullName: string;
}

export interface ViewAsHistoryEntry {
  occurredAt: string;
  action: string;
  entityType: string;
  entityId: string;
  byEmployeeId: string | null;
  byFullName: string | null;
  byEmail: string | null;
}

export function useAssumableEmployees(enabled: boolean) {
  return useQuery({
    queryKey: ["auth", "view-as", "candidates"],
    queryFn: () =>
      api.get<{ candidates: AssumableEmployee[] }>("/auth/view-as/candidates"),
    enabled,
    staleTime: 60_000,
  });
}

export function useStartViewAs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (employeeId: string) =>
      api.post<{ ok: true; expiresAt: number }>(
        `/auth/view-as/${employeeId}`,
        {},
      ),
    onSuccess: async () => {
      await qc.resetQueries();
    },
  });
}

export function useEndViewAs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ ok: true }>("/auth/view-as"),
    onSuccess: async () => {
      await qc.resetQueries();
    },
  });
}

/** Wer in meinem Namen gehandelt hat — für die betroffene Person selbst. */
export function useViewAsHistory() {
  return useQuery({
    queryKey: ["auth", "view-as", "history"],
    queryFn: () =>
      api.get<{ entries: ViewAsHistoryEntry[] }>("/auth/view-as/history"),
    staleTime: 30_000,
  });
}
